import type React from 'react';
import { useEffect, useRef } from 'react';
import { ForgeButtonToggle, ForgeButtonToggleGroup, ForgeIcon, ForgeTooltip } from '@tylertech/forge-react';
import { handleEnter } from 'common/dom_helpers/keyPressHelpers';
import { I18n } from 'common/i18n';

const scope = 'shared.visualizations.charts.common.view_tabs';

/**
 * One tab of the chart/table tab selector on the bottom left of each visualization.
 */
function ViewTab({
  iconName,
  selected,
  tabName,
  visualizationId,
  visualizationType,
}: {
  iconName: string;
  selected: boolean;
  tabName: 'visualization' | 'summary-table';
  visualizationId: string;
  visualizationType: 'map' | 'chart' | 'table';
}) {
  return (
    <ForgeButtonToggle
      aria-controls={`${tabName}-panel-${visualizationId}`}
      aria-label={I18n.t(`aria_label.${visualizationType}`, { scope })}
      data-testid={`${tabName}-button-${visualizationId}`}
      id={`${tabName}-button-${visualizationId}`}
      selected={selected}
      value={tabName}
    >
      <ForgeIcon name={iconName} />
    </ForgeButtonToggle>
  );
}

/**
 * Renders tabs for switching between the summary table and chart or map view of a visualization.
 * See ViewTabsContainer for the tab panel implementation.
 */
export function ViewTabs({
  disabled = false,
  isMap,
  onClickTableTab,
  onClickVisualizationTab,
  shouldShowTable,
  visualizationId,
}: {
  disabled?: boolean;
  isMap: boolean;
  onClickTableTab: () => void;
  onClickVisualizationTab: () => void;
  shouldShowTable: boolean;
  visualizationId: string;
}) {
  const visualizationType = isMap ? 'map' : 'chart';
  const groupRef = useRef<React.ComponentRef<typeof ForgeButtonToggleGroup>>(null);

  const handleTabChange = (event: CustomEvent) => {
    if (event.detail === 'visualization') {
      onClickVisualizationTab();
    } else {
      onClickTableTab();
    }
  };

  useEffect(() => {
    const group = groupRef.current as unknown as HTMLElement | null;

    if (!group) {
      return undefined;
    }

    /**
     * ForgeButtonToggle's own keydown handling only recognizes Space, so Enter is wired up here.
     * This is fixed in Forge 3.15 and this can be removed once we are able to upgrade.
     */
    const handleEnterKey = handleEnter((event: KeyboardEvent) => (event.target as HTMLElement).click(), true);

    group.addEventListener('keydown', handleEnterKey);
    return () => group.removeEventListener('keydown', handleEnterKey);
  }, []);

  return (
    <ForgeButtonToggleGroup
      dense
      disabled={disabled}
      mandatory={true}
      on-forge-button-toggle-group-change={handleTabChange}
      outlined={false}
      ref={groupRef}
      theme={'primary'}
    >
      <ViewTab
        iconName={isMap ? 'map' : 'bar_chart'}
        selected={!shouldShowTable}
        tabName="visualization"
        visualizationId={visualizationId}
        visualizationType={visualizationType}
      />
      <ForgeTooltip placement="bottom">{I18n.t(visualizationType, { scope })}</ForgeTooltip>
      <ViewTab
        iconName="table"
        selected={shouldShowTable}
        tabName="summary-table"
        visualizationId={visualizationId}
        visualizationType="table"
      />
      <ForgeTooltip placement="bottom">{I18n.t('table', { scope })}</ForgeTooltip>
    </ForgeButtonToggleGroup>
  );
}
